import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import { PanelsTopLeft, Route } from 'lucide-react';
import { useActivity } from '../../../lib/activity';
import { useAllWorkspaceApps } from '../../../lib/app-shares';
import { useAgentWorkspaceFiles } from '../../../lib/agent-workspace-files';
import { creatorLabel, creatorOf, isHiddenFromHomeFiles } from '../../../lib/file-browser';
import { useAgentName } from '../../../lib/use-agent-name';
import { useWorkspaceGoals } from '../../../lib/goals';
import { useRoutines } from '../../../lib/routines';
import { useOutreachesRead, useReceivedOutreaches, useSentOutreaches } from '../../../lib/outreaches';
import {
  buildHome3Feed,
  allListState,
  homeFileKey,
  HOME3_DESTINATIONS,
  resolveFeedState,
  summaryNumbers,
  type Home3Destination,
  type Home3DestinationFilter,
  type Home3ReadOutcome,
} from '../../../lib/home3-feed';
import { useContainerNarrow, HOME_STACK_BREAKPOINT_PX } from '../../../lib/use-container-narrow';
import { useViewportNarrow } from '../../../lib/use-narrow';
import { useLoadBi } from '../../../lib/use-load-bi';
import { usePageLoadBi } from '../../../lib/use-page-load-bi';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useMyAgents } from '../../../lib/my-agents';
import { useShowMobileAppCard } from '../../../lib/mobile-app-status';
import { firstNameFor, useMe } from '../../../lib/me';
import { greetingWithoutName } from '../../../lib/greeting-without-name';
import { useBasePath } from '../../../lib/use-base-path';
import { invalidateAgentTaskQueries } from '../../../lib/agent-task-invalidation';
import { queryClient, queryKeys } from '../../../lib/query-client';
import { unsettledTasksRefetchInterval, useTasks } from '../../../lib/tasks';
import { useTranslation } from '../../../lib/i18n';
import { useWorkspaces } from '../../../lib/workspaces';
import { useContactBook } from '../../../lib/contact-book';
import { useHome3CategoryVisits } from '../../../lib/use-last-visited';
import { newSince } from '../../../lib/summary-freshness';
import { AgentsIslandBadge, useAgentsPillReady } from '../AgentsIslandBadge';
import { useChipSlotHolds } from '../chip-morph/ChipSlotContext';
import { GreetingDate } from '../DashboardView/GreetingDate';
import { MapHome } from '../home2/map/MapHome';
import { useActivityMapTabsHidden } from '../../../lib/home-variant';
import { IconButton, Typography } from '../ui';
import { Home3Categories } from './Home3Categories';
import { Home3CategoryView } from './Home3CategoryView';
import { Home3ConnectorsRail } from './Home3ConnectorsRail';
import { Home3MobileAppCard } from './Home3MobileAppCard';
import { Home3Feed, HOME3_PAGE } from './Home3Feed';
import { Home3Summary } from './Home3Summary';
import styles from './Home3Body.module.css';

/**
 * How often a QUIET feed re-reads itself — the case `unsettledTasksRefetchInterval` deliberately does
 * not cover. Slower than its 15s on purpose: nothing is running, so this is only here to catch work
 * that STARTED somewhere else (the chat beside this screen), not to track work in flight.
 */
const HOME3_QUIET_REFRESH_MS = 30_000;

/**
 * How many extra activity pages the home will pull. 30 rows a page, so this is up to ~150 actions —
 * generous for a glance that shows 6, and a hard stop on an account whose history would otherwise be
 * paged in full behind the first paint.
 */
const MAX_ACTIVITY_PAGES = 4;

/**
 * The two views this page can show, named by the homepage slugs they mirror
 * (`lib/homepage-preference.ts`) so the switch can reuse those settings' own labels.
 *
 * They are a view of THIS page, not the saved homepage: switching must not rewrite the reader's home
 * preference as a side effect of looking at the map for a moment.
 */
type Home3View = 'task-list' | 'activity-map';

/** The DOM id of what the tiles open — the "All" list or a category's page; the tiles' `aria-controls`. */
const FEED_REGION_ID = 'home3-activity';

/**
 * **The Overview home** — the `task-list` home choice (`lib/homepage-preference.ts`).
 *
 * Greeting and the big date · the Overview/Activity Map switcher · the Daily Summary card · the Spaces
 * strip · the six category cards over the activity list · the connectors rail · the composer.
 *
 * ## The layout is two columns, and the rail is the reason
 *
 * The design (Figma `19106:277126`) splits the page 913 : 335 — the activity board on the left, the
 * connectors rail on the right — where this screen used to be one column with connectors stacked at
 * the foot. The rail is a *reference* column: it is what the agents are working THROUGH, read
 * alongside what they did, rather than a section the reader scrolls past the activity to reach.
 *
 * ## Where its rows come from
 *
 * Three reads the app already makes, merged by `lib/home3-feed.ts` into one list of rows:
 *
 * - **tasks** (`useTasks`, which also folds in each task's SENT outreaches) — the state, the origin and
 *   the channel all come off the task, so a row's badge is the same badge the Tasks screen gives it;
 * - **the agent activity log** (`useActivity`) — what an agent actually DID, for the acts that are not
 *   a task of their own. An action belonging to a task already in the list is dropped rather than
 *   doubling it;
 * - **apps** (`useAllWorkspaceApps`) — an app an agent built in the window, which is the only honest
 *   source for the Apps destination.
 *
 * Nothing here is illustrative: there is no constant list of events, the tiles' unread discs are
 * counted over these same rows, and the summary card's two numbers are `summaryNumbers` over them.
 *
 * ## The destination selection lives HERE, not in either child
 *
 * `Home3Categories` draws the seven tiles. "All" opens `Home3Feed`, every kind of item newest first; any
 * other tile opens that category's own page across every Space (`Home3CategoryView`). The selection is
 * this screen's, so a tile and what is open under it can never disagree.
 *
 * ## Narrow means the PANE, not the window
 *
 * The row cap (3 on a narrow pane, 6 on a wide one) and the card grid's columns are measured on this
 * screen's own container, because the thing that narrows this page is the docked chat — it takes 370px
 * out of the content pane and leaves `window.innerWidth` untouched. The workspaces row is the one
 * deliberate exception: it is MOBILE-WEB ONLY (the desktop Overview drops it — the category cards carry
 * Spaces there), so its show/hide is per-VIEWPORT and reads `useViewportNarrow` (the true window), NOT
 * the container squeeze — a desktop with the docked chat open is still a desktop. It is the SYNCHRONOUS
 * viewport read (not `useNarrow`) so a mobile load never first paints the desktop layout — no strip and
 * a 32px gap — and then shifts; this screen is client-only, so reading matchMedia on first render is safe.
 */
export function Home3Body() {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);
  const narrow = useContainerNarrow(rootRef, HOME_STACK_BREAKPOINT_PX);
  const [destination, setDestination] = useState<Home3DestinationFilter>(null);

  const me = useMe();
  const name = firstNameFor(me);
  /**
   * The title is the time-of-day GREETING — "Good morning, {name}" — the same sentence the catch-up
   * home's `GreetingDate` draws, from the same three keys and the same 12/18 boundaries.
   *
   * It used to be "Your activity, {name}". Design asked for the greeting, so the screen now opens the
   * way every other home does.
   *
   * Two things this copies rather than imports, for the reason `firstNameFor` is copied (see the note
   * there): extracting the rule would put this experiment's refactor on the live catch-up home, which
   * no flag gates. `test/home3-overview.test.ts` pins the two expressions together instead.
   *
   * - **The hour is read after mount**, not during render. The server has no idea what time it is where
   *   the reader is, so a greeting resolved during SSR can disagree with the client's across an hour
   *   boundary; `suppressHydrationWarning` on the text's own parent is what `GreetingDate` does for
   *   exactly this.
   * - **The copy bakes the name in**, so with no name yet the whole NAME CLAUSE is removed — not just
   *   a trailing comma, which left Japanese reading `おはようございます、さん` and Korean
   *   `좋은 아침이에요, 님`: the honorific greeting the reader as "Mr." with nobody in front of it.
   *   `greetingWithoutName` is that rule, locale-agnostic and pinned over all 37 bundles.
   */
  const [greetingHour, setGreetingHour] = useState(() => new Date().getHours());
  useEffect(() => setGreetingHour(new Date().getHours()), []);
  const greetingKey =
    greetingHour < 12 ? 'home.greeting.morning' : greetingHour < 18 ? 'home.greeting.afternoon' : 'home.greeting.evening';
  const greeting = name ? t(greetingKey, { firstName: name }) : greetingWithoutName(t, greetingKey);
  // The TRUE viewport, not the container squeeze — the docked chat is a desktop thing, and a desktop
  // with the chat open is still a desktop. Synchronous, so a mobile load paints the narrow rhythm on
  // the first frame instead of flashing the desktop layout.
  const viewportNarrow = useViewportNarrow();
  // `specs.symphony.SymphonyActivityMapHideTabsWeb`: on the desktop this home is the task list alone —
  // no switch, and no map view to be left on. Mobile web keeps both.
  const tabsHidden = useActivityMapTabsHidden(!viewportNarrow);
  // Which view this page is showing. LOCAL state, deliberately — see the switch's own note.
  const [chosenView, setView] = useState<Home3View>('task-list');
  const view: Home3View = tabsHidden ? 'task-list' : chosenView;
  const router = useRouter();
  const basePath = useBasePath();
  /**
   * Drop the map's own URL state when leaving its view.
   *
   * The hosted map shares this page's route (`/`), and its desktop tile tab rides the URL —
   * `MapHome`'s `setTab` writes `?tab=<bucket>` (plus `person`/`pkind` inside Outreach). Switching
   * back is local state, so without this the query outlives the view: the home URL keeps a `?tab=`
   * that shows nothing, and re-opening the map reads it back and starts on a tile tab with an inline
   * section instead of on the map.
   *
   * Cleared on LEAVE rather than on enter, so a deliberate deeplink into a tab still works.
   *
   * ⚠️ The `as` must carry the active mount prefix. A bare `{ pathname }` replace drops `/orion-web`
   * and lands on the root mount — the same trap `MapHome.setTab` documents, whose construction this
   * mirrors.
   */
  const clearMapQuery = useCallback(() => {
    const query = { ...router.query };
    const dropped = ['tab', 'person', 'pkind'].filter((key) => key in query);
    if (dropped.length === 0) return;
    for (const key of dropped) delete query[key];
    const qs = new URLSearchParams(
      Object.entries(query).flatMap(([k, v]) => (typeof v === 'string' ? [[k, v]] : [])),
    ).toString();
    const as = `${basePath}${router.pathname === '/' ? '/' : router.pathname}${qs ? `?${qs}` : ''}`;
    void router.replace({ pathname: router.pathname, query }, as, { shallow: true, scroll: false });
  }, [router, basePath]);

  const pillReady = useAgentsPillReady();
  const pillHere = useChipSlotHolds('pill');
  const reserving = pillReady && !pillHere;

  const { state: taskState } = useTasks();
  const activity = useActivity();
  const team = useMyAgents();
  const workspacesState = useWorkspaces();
  const workspaces = workspacesState.status === 'ready' ? workspacesState.workspaces : [];
  const workspaceIds = useMemo(() => workspaces.map((w) => w.id).filter(Boolean), [workspaces]);
  const appsState = useAllWorkspaceApps(workspaceIds);
  // The messages people have SENT the account. Shares the cache entry the task list's own
  // sent-outreach fold already fills, so the Messages category costs no extra request.
  const received = useReceivedOutreaches();
  // …and the threads the account SENT. A reply is NOT a received outreach — it lands as a message
  // inside the sent thread — and a message sent from a chat is folded onto no task, so without this
  // the Messages category was empty on an account with three live WhatsApp threads (measured).
  const sentThreads = useSentOutreaches();
  const outreachRead = useOutreachesRead();
  // The goals, routines and files the account HOLDS — each its category's own rows, so a category lists
  // what is in it and not only the tasks that pointed at it. The same cache entries the Goals, Routines
  // and Files screens read, so this costs a request only when none of them has run yet.
  const goalsState = useWorkspaceGoals();
  const { state: routinesState } = useRoutines();
  const filesState = useAgentWorkspaceFiles();
  const assistantName = useAgentName().short;

  // The feed's own load pair: it settles when the two reads that can produce a row have answered. Apps
  // are deliberately not in the wait — an app is a rare row, and holding the whole feed on one read per
  // Space would put the slowest Space in front of every other row on the screen — so they report their
  // own pair instead of being folded into this one.
  const tasksOutcome = { loading: taskState.kind === 'loading', error: taskState.kind === 'error' };
  const actionsOutcome = { loading: activity.state.kind === 'loading', error: activity.state.kind === 'error' };
  const readState = resolveFeedState(tasksOutcome, actionsOutcome);
  useLoadBi('home3-feed', { isLoading: readState === 'loading', isError: readState === 'error' });
  useLoadBi('home3-apps', { isLoading: appsState.status === 'loading', isError: appsState.status === 'error' });
  // Each category's own read, so a slow or failed one is told apart from an empty category — in the
  // "All" list (`allListState`) and in the load beacons.
  const goalsRead = { loading: goalsState.status === 'loading', error: goalsState.status === 'error' };
  const routinesRead = { loading: routinesState.kind === 'loading', error: routinesState.kind === 'error' };
  const filesRead = { loading: filesState.status === 'loading', error: filesState.status === 'error' };
  const appsRead = { loading: appsState.status === 'loading', error: appsState.status === 'error' };
  const itemReads: Partial<Record<Home3Destination, Home3ReadOutcome>> = {
    goals: goalsRead,
    routines: routinesRead,
    files: filesRead,
    apps: appsRead,
    outreach: outreachRead,
  };
  useLoadBi('home3-goals', { isLoading: goalsRead.loading, isError: goalsRead.error });
  useLoadBi('home3-routines', { isLoading: routinesRead.loading, isError: routinesRead.error });
  useLoadBi('home3-files', { isLoading: filesRead.loading, isError: filesRead.error });

  // The page view. The home had none at all — `pages/index.tsx` fires no `usePageLoadBi` — so this is
  // the first honest page-load beacon for it, held until the feed's reads settle so `isSuccessful`
  // reports what actually happened rather than the optimistic default.
  usePageLoadBi(
    { page_name: HOME3_PAGE },
    { enabled: readState !== 'loading', isSuccessful: readState === 'ready', trackBack: true },
  );

  // `useActivity` pages 30 rows at a time. The rows only need the newest few, which the first page
  // already holds, but the counts (chips, footnote) are over everything loaded — so pull a few more
  // pages, BOUNDED by `MAX_ACTIVITY_PAGES`. This is a glance, not the Activity Log, and the full
  // history is one click away on the screen this feed links to.
  const [pagesPulled, setPagesPulled] = useState(0);
  useEffect(() => {
    if (activity.state.kind !== 'ready' || !activity.hasMore || activity.loadingMore) return;
    if (pagesPulled >= MAX_ACTIVITY_PAGES) return;
    if (activity.loadMore()) setPagesPulled((n) => n + 1);
  }, [activity, pagesPulled]);

  const events = useMemo(
    () =>
      buildHome3Feed({
        tasks: taskState.kind === 'ready' ? taskState.tasks : [],
        actions: activity.state.kind === 'ready' ? activity.state.actions : [],
        apps: appsState.status === 'ready' ? appsState.apps : [],
        received: received.map((entry) => ({
          id: entry.row.id ?? '',
          senderName: entry.row.senderName ?? entry.row.title,
          summary: entry.row.summary,
          channel: entry.channel,
          updatedDate: entry.row.updatedDate,
          status: entry.row.status,
          // Straight through to `OutreachThreadModal`, which is where the correspondence, the reply
          // and a shared file's own link live.
          task: entry.row,
        })),
        sent: sentThreads.map((entry) => ({
          id: entry.summary.id,
          recipientName: entry.summary.recipientName,
          channel: entry.summary.channel,
          replied: entry.summary.replied,
          awaitingReply: entry.summary.awaitingReply,
          messages: entry.summary.messages,
          thread: entry.summary,
        })),
        goals: goalsState.status === 'ready' ? goalsState.goals : [],
        routines: routinesState.kind === 'ready' ? routinesState.routines : [],
        files:
          filesState.status === 'ready'
            ? filesState.items.filter((file) => !isHiddenFromHomeFiles(file)).map((file) => {
                const creator = creatorOf(file);
                return {
                  id: homeFileKey(file),
                  name: file.name,
                  workspaceId: file.workspaceId,
                  agentId: creator?.type,
                  agentName: creator ? creatorLabel(creator, t, assistantName) : undefined,
                  updatedDate: file.updatedDate,
                  createdDate: file.createdDate,
                };
              })
            : [],
        agents: team.status === 'ready' ? team.team.agents : [],
        now: Date.now(),
      }),
    [taskState, activity.state, appsState, received, sentThreads, goalsState, routinesState, filesState, team, t, assistantName],
  );

  const listState = allListState(readState, Object.values(itemReads), events.length === 0);

  // Space id → name, for the rows' second line ("Email | Pizza Milano"). The screen already reads the
  // workspaces for the Spaces strip, so this is a lookup rather than a new read.
  const spaceNames = useMemo(() => Object.fromEntries(workspaces.map((w) => [w.id, w.name])), [workspaces]);

  const agentNames = useMemo(
    () => Object.fromEntries(team.status === 'ready' ? team.team.agents.map((a) => [a.id, a.name]) : []),
    [team],
  );
  // The card is DAILY while the feed has no window — `summaryNumbers` takes `now` and scopes itself, so the "Overnight…" headline keeps meaning what it says.
  const { closed, moving } = useMemo(() => summaryNumbers(events, Date.now()), [events]);

  // ## A floor under the push, for the one screen the reader SITS on
  //
  // `useTasks` already re-reads every 15s while anything is unsettled, and the AGENT_TASK duplexer
  // frame is what makes a change feel instant. Both have a hole that meets on this screen: the timer
  // switches OFF once nothing is unsettled — an EMPTY feed included — and the push is https-only and
  // rides a four-hop chain (service write → domain event → harness consumer → frame). So on a quiet
  // home, a task the assistant creates in the chat beside it can arrive by push or not at all, and
  // "it doesn't update until I refresh" is the report.
  //
  // This runs only where the shared timer stands down, so the two never double up, and only while the
  // activity board is the shown view and the document is visible. One invalidate per tick, which
  // React Query turns into one refetch per active key.
  const tasksPolling = unsettledTasksRefetchInterval(taskState.kind === 'ready' ? taskState.tasks : undefined) !== false;
  useEffect(() => {
    if (view !== 'task-list' || tasksPolling) return undefined;
    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      invalidateAgentTaskQueries();
      void queryClient.invalidateQueries({ queryKey: queryKeys.activity });
    }, HOME3_QUIET_REFRESH_MS);
    return () => clearInterval(timer);
  }, [view, tasksPolling]);

  // ## The quick-return header — mobile web
  //
  // The header is NOT sticky: scrolling the list DOWN slides it away WITH the content (1:1, so it can
  // never lag the finger), and the moment you scroll UP it SNAPS back pinned — the catch-up home's own
  // quick-return (`SymphonyHomeView.nextHeaderReveal` / `ActivityMapScreen` on iOS, `ActivityMapScreen`
  // on Android), recreated here so the three surfaces match. At the top it is fully shown. Only the
  // snap-back is animated (a short `transform` transition); the down-slide carries no transition.
  //
  // Scoped to the PHONE viewport (`viewportNarrow`) and the task-list view — the hosted activity map
  // carries its own scrolling, and on the desktop the header stays in the two-column grid. The CSS var
  // and `data-float` drive `.header[data-float]` under the `max-width: 719px` block in the stylesheet;
  // the write goes straight to the DOM on a rAF, never through React state, so a scroll never re-renders
  // the screen (PERFORMANCE.md rule 14f).
  useEffect(() => {
    const header = headerRef.current;
    const root = rootRef.current;
    if (!header || !root) return;
    if (!viewportNarrow || view !== 'task-list') {
      header.removeAttribute('data-float');
      header.style.removeProperty('--home3-header-reveal');
      return;
    }

    // The scrolling ancestor is the content pane (`.orion-content-pane`, `overflow-y: auto`); walk up
    // to it rather than hard-coding the two wrappers DashboardPane puts between it and `.root`.
    let scroller: HTMLElement | null = root.parentElement;
    while (scroller) {
      const oy = getComputedStyle(scroller).overflowY;
      if (oy === 'auto' || oy === 'scroll') break;
      scroller = scroller.parentElement;
    }
    if (!scroller) return;
    const pane = scroller;

    let reveal = 0;
    let last = pane.scrollTop;
    let frame = 0;
    header.setAttribute('data-float', 'true');
    header.style.setProperty('--home3-header-reveal', '0px');

    const apply = () => {
      frame = 0;
      const top = pane.scrollTop;
      const delta = top - last;
      last = top;
      const hide = header.offsetHeight;
      // Quick-return: at the top → shown; scrolling UP → snap pinned; scrolling DOWN → slide with the
      // content (1:1). Mirrors the native `ActivityMapScreen` header. The down clamp is theirs:
      // Math.min(0, Math.max(-hide, reveal - delta)).
      if (top <= 0) {
        reveal = 0;
      } else if (delta < 0) {
        reveal = 0;
      } else {
        reveal = Math.min(0, Math.max(-hide, reveal - delta));
      }
      // Animate ONLY the snap-back (scroll-up / return-to-top); the down-slide tracks the finger with no
      // transition so it can never lag.
      header.style.transition = reveal === 0 && top > 0 ? 'transform 0.2s ease-out' : 'none';
      header.style.setProperty('--home3-header-reveal', `${reveal}px`);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(apply);
    };

    pane.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      pane.removeEventListener('scroll', onScroll);
      header.removeAttribute('data-float');
      header.style.removeProperty('--home3-header-reveal');
    };
  }, [view, viewportNarrow]);

  // Only for an account CONFIRMED to have never had the app — the same gate as the catch-up home
  // (see `showMobileAppCard`). Desktop only: a QR on the phone it would install onto is a dead end.
  // Per-VIEWPORT for the same reason as the workspaces row — a desktop with the docked chat open is
  // still a desktop.
  const neverHadMobileApp = useShowMobileAppCard();
  const showMobileApp = neverHadMobileApp && !viewportNarrow;

  // A card is a TOGGLE: the same card again clears back to the whole window, which is the one way out
  // of a filtered list that does not require finding a separate control.
  // ## The tiles' discs: what arrived in each category since that category was last opened
  //
  // Counted over `events` — the same rows the list and the footnote describe — so a tile can never
  // report more than the list below it is able to show.
  //
  // Per-category stamps, not one for the screen — pressing Tasks must clear the Tasks disc and leave
  // the rest alone, which a single "when were you last here" cannot express. `newSince` supplies the
  // first-visit rule (the last 24 hours) and ignores a stamp from the future, so a wrong device clock
  // cannot suppress a disc for ever.
  const { stamps, mark } = useHome3CategoryVisits();
  const unread = useMemo(() => {
    const now = Date.now();
    const out = Object.fromEntries(HOME3_DESTINATIONS.map((d: Home3Destination) => [d, 0])) as Record<Home3Destination, number>;
    // Until the stamps have been read there is no honest count — `Home3Categories` draws no disc
    // while `readState` is not ready, and this keeps the arithmetic from inventing one either.
    if (!stamps) return out;
    for (const event of events) {
      const since = newSince(stamps[event.destination], now);
      // `firstAt`, never `at` — see the field's own note. A running task keeps moving `at`, so
      // counting against it brought the disc back minutes after the reader cleared it, on work they
      // had already looked at. A row rings ONCE, when it first appears; a row whose first appearance
      // is unknown (`firstAt` absent) rings never, because there is no stable moment to compare.
      if (event.firstAt !== undefined && event.firstAt > since) out[event.destination] += 1;
    }
    return out;
  }, [events, stamps]);

  // The Messages tile wears the faces of the TWO most recent senders — the design's mark is a cluster
  // of two, so it takes two (`Home3Categories`'s own note). The contact book is already the read
  // behind the Outreach screen, so this is a lookup rather than a new request; a short list is padded
  // by the tile with the design's own mark rather than changing its shape.
  const book = useContactBook('');
  const messengers = useMemo(() => {
    if (book.kind !== 'ready') return [];
    return book.contacts
      .filter((c) => (c.inbound ?? []).length > 0)
      .sort((a, b) => Date.parse(b.lastActivityAt ?? '') - Date.parse(a.lastActivityAt ?? ''))
      .slice(0, 2)
      .map((entry) => {
        const card = entry.contact;
        return {
          name: card?.displayName ?? '',
          photoUrl: card?.effectiveAvatarUrl || undefined,
          // The person's own id where the book has one, so their ramp colour survives a rename.
          seed: card?.personId || entry.recipientId || card?.displayName || '',
        };
      })
      .filter((m) => m.name !== '' || m.photoUrl !== undefined);
  }, [book]);

  const pickDestination = (next: Home3Destination) => {
    const value = destination === next ? null : next;
    setDestination(value);
    // Pressing a tile is the reader looking at that category, so its disc clears — on the spot, and
    // only that one's.
    if (value) mark(value);
    logPageClickBi({ page_name: HOME3_PAGE, button_name: `filter_destination_${value ?? 'none'}` });
  };

  const pickAll = () => {
    setDestination(null);
    logPageClickBi({ page_name: HOME3_PAGE, button_name: 'filter_destination_all' });
  };

  // The four header pieces, extracted so the DESKTOP grid and the MOBILE-WEB layout can place them
  // differently without duplicating markup. On mobile web ONLY the greeting + date is sticky (Figma
  // 20107-61328) — it quick-returns — while the island + view switch scroll as content, exactly as the
  // native apps do (the "menu" there is this app's always-present AppFrame hamburger, so it is not part
  // of this bar). Order here is island → title → switch → date, which `home3-overview.test.ts` pins.
  const islandEl = (
    <div className={`${styles.islandRow}${reserving ? ` ${styles.islandRowReserved}` : ''}`}>
      {reserving ? null : (
        <GenericEventsRouteProvider value="agents-island">
          <AgentsIslandBadge side="left" />
        </GenericEventsRouteProvider>
      )}
    </div>
  );
  const greetingEl = (
    <Typography variant="h2" color="primary" component="h1" className={styles.title} suppressHydrationWarning>
      {greeting}
    </Typography>
  );
  const viewSwitchEl = tabsHidden ? null : (
    <div className={styles.viewSwitch} role="radiogroup" aria-label={t('home.overview.viewSwitch')}>
      {(
        [
          { value: 'task-list', label: t('settings.homepage.taskList'), Icon: PanelsTopLeft },
          { value: 'activity-map', label: t('settings.homepage.activityMap'), Icon: Route },
        ] as const
      ).map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          className={`${styles.viewSwitchBtn}${view === value ? ` ${styles.viewSwitchOn}` : ''}`}
          aria-label={label}
          role="radio"
          aria-checked={view === value}
          onClick={() => {
            logPageClickBi({ page_name: HOME3_PAGE, button_name: `switch_view_${value}` });
            if (value === 'task-list') clearMapQuery();
            setView(value);
          }}
        >
          <Icon size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
        </button>
      ))}
    </div>
  );
  // The big two-line date — the shared mark that owns the browser-locale day/month order + hydration
  // treatment (`test/home-date-browser-locale.test.ts`).
  const dateEl = (
    <div className={styles.date}>
      <GreetingDate part="date" />
    </div>
  );

  return (
    <div ref={rootRef} className={styles.root} data-testid="home3">
      {/* ## ONE header row, independent of the body below it (Figma 20325:122322)
          
              [ island ]                       [ ⇄ switch ]  [ greeting  date ]
          
          It used to sit on the body's own two columns (board : rail). The map view has no rail, so the
          header changed grid when the reader switched views and the switch and the date jumped. Now
          the header asks nothing about the body, and both views draw it identically.
          
          So on a narrow pane only the greeting + date is sticky and quick-returns; the island + view
          switch scroll as content below it. On desktop the header stays master's static grid, since the
          quick-return is native + mobile-web only (Figma 20107-61328). */}
      {viewportNarrow ? (
        // MOBILE WEB: only the greeting + date is sticky and quick-returns (Figma 20107-61328); the
        // island + view switch scroll as content below it — parity with iOS/Android. The AppFrame
        // hamburger (always-present chrome) is the "menu" the native top bar has, so the greeting + date
        // sit to its right with a hamburger clearance.
        <>
          <header ref={headerRef} className={styles.mobileTopBar}>
            {greetingEl}
            {dateEl}
          </header>
          <div className={styles.mobileIslandRow}>
            {islandEl}
            {viewSwitchEl}
          </div>
        </>
      ) : (
        // DESKTOP: unchanged by the quick-return — master's static header grid. The island keeps the
        // first line; the view switch + greeting/date group sits at the inline end (and wraps under the
        // island on a narrow DESKTOP pane).
        <header className={styles.header}>
          {islandEl}
          <div className={styles.headerEnd}>
            {viewSwitchEl}
            <div className={styles.greetingDate}>
              {greetingEl}
              {dateEl}
            </div>
          </div>
        </header>
      )}

      {view === 'activity-map' ? (
        // The REAL activity map, hosted — not a second one built for this page. It is the same
        // component the `activity-map` home choice renders, in `embedded` mode so it drops the map's
        // own header and composer, which this page already carries. Mounted only while the map is the
        // chosen view: it brings a canvas renderer and its own live layer, and neither should run
        // behind the activity board.
        <div className={styles.mapView}>
          <MapHome view="activity-map" landOn="brief" embedded />
        </div>
      ) : (
      <div className={styles.columns}>
        {/* `.mainWide` lifts the brief → section gap to 32 on the desktop only; mobile web (narrow) keeps
            the base `.main` 24 rhythm untouched. Driven by the true viewport. */}
        <div className={`${styles.main}${viewportNarrow ? '' : ` ${styles.mainWide}`}`}>
          {/* Not collapsible — see the component's own note. */}
          <Home3Summary readState={readState} closed={closed} moving={moving} />

            {/* ## The tiles and the list are siblings of the brief, on the column's own rhythm
                
                There is no head row above them: the "All" list and each category's page carry their
                own. With the head went the `.board` / `.boardTop` wrappers: with nothing between the brief,
                the tiles and the list, the column's own gap is the whole spacing rule, and a wrapper
                that only re-states its parent's gap is a second number to keep in step. */}
            <Home3Categories
              unread={unread}
              selected={destination}
              onSelect={pickDestination}
              onSelectAll={pickAll}
              readState={readState}
              listId={FEED_REGION_ID}
              messengers={messengers}
            />

            {destination ? (
              <Home3CategoryView key={destination} destination={destination} regionId={FEED_REGION_ID} />
            ) : (
              <Home3Feed
                events={events}
                narrow={narrow}
                viewportNarrow={viewportNarrow}
                agentNames={agentNames}
                spaceNames={spaceNames}
                readState={listState}
                regionId={FEED_REGION_ID}
              />
            )}
          </div>

          {/* The reference column: the download-the-app card. It renders nothing at all when there is
              nothing to show, and the grid collapses to one column under it. */}
          <aside className={styles.rail}>{showMobileApp && <Home3MobileAppCard />}</aside>

          {/* Recommended connectors, across the page's full width at its foot — on "All" only. */}
          {!destination && (
            <div className={styles.connectorsBand}>
              <Home3ConnectorsRail />
            </div>
          )}
      </div>
      )}

      {/* ## No docked composer here — the chat is the app's own floating launcher
          
          The design puts a round chat bubble at the page's bottom-right (Figma 19106:277823) and no
          composer bar in the page at all. `FloatingChat` is that bubble, and it is AppFrame chrome, so
          this screen simply stops drawing a second input.
          
          It cost the page its foot, too: the docked bar sat over the bottom of the scroll, which is
          where the "47 events across 7 agents" line lives — that line was not missing, it was covered.
          
          The narrow breakpoint never had the bar (the launcher was always the one door there), so this
          makes the two widths agree rather than diverging further. */}
    </div>
  );
}
